<script lang="ts" setup generic="P, R">
import type { SelectInstance, SelectV2Instance, } from "element-plus"
import type { ModelValue, OptionItem, Props, } from "./types.ts"
import { omit, } from "lodash-es"
import { defaultProps, } from "./types.ts"
import { useOptions, } from "./useOptions.ts"
// 使用了哪些组件，需要引入对应的样式文件，可将常用的在ApiSelect中引入
import "element-plus/es/components/select-v2/style/index"
import "element-plus/es/components/tree-select/style/index"

const props = withDefaults(defineProps<Props<P, R>>(), defaultProps,)

const emits = defineEmits<{
  change: [val: ModelValue,]
  changeOption: [option?: OptionItem,]
  paste: [e: ClipboardEvent, options: OptionItem[], val?: string,]
}>()

interface DynamicSlots {
  [key: string]: unknown
}

const modelValue = defineModel<ModelValue>({ required: true, },)

const attrs: Record<string, unknown> = useAttrs()
const slots = useSlots() as DynamicSlots

const { options, loading, formatOptions, fetchOptions, } = useOptions<P, R>(props,)

const bindProps = computed(() => {
  const omitKeys = Object.keys(defaultProps,)
  const omitProps = omit(props, omitKeys,)
  const optionsComponents = ["ElCascader", "ElSelectV2", "VxeSelect",]
  return {
    [props.modelPropName]: unref(modelValue,),
    [`onUpdate:${props.modelPropName}`]: (value: ModelValue,) => {
      const { apiConfig, } = props
      if (apiConfig?.config?.value) {
        const option = formatOptions.value.find(item => item.value === value,)
        emits("changeOption", option,)
      }
      emits("change", value,)
      modelValue.value = value
    },
    ...attrs,
    ...omitProps,
    options: optionsComponents.includes(props.component.name || "",) ? unref(formatOptions,) : undefined,
    loading: unref(loading,),
  }
},)

const componentRef = ref<unknown>()

// 监听 el-select 的粘贴事件
function handlePaste(e: ClipboardEvent,) {
  const { clipboardData, } = e
  if (clipboardData) {
    const text = clipboardData.getData("text",)
    emits("paste", e, unref(formatOptions,), text,)
  }
}

onMounted(() => {
  if (props.component.name === "ElSelect") {
    const selectInstance = componentRef.value as SelectInstance
    selectInstance.inputRef?.addEventListener("paste", handlePaste,)
  }
  if (props.component.name === "ElSelectV2") {
    const selectInstance = componentRef.value as SelectV2Instance
    selectInstance.inputRef?.addEventListener("paste", handlePaste,)
  }
},)

onBeforeUnmount(() => {
  if (props.component.name === "ElSelect") {
    const selectInstance = componentRef.value as SelectInstance
    selectInstance.inputRef?.removeEventListener("paste", handlePaste,)
  }
  if (props.component.name === "ElSelectV2") {
    const selectInstance = componentRef.value as SelectV2Instance
    selectInstance.inputRef?.removeEventListener("paste", handlePaste,)
  }
},)

defineExpose({
  options,
  formatOptions,
  fetchOptions,
  componentRef,
},)
</script>

<template>
  <component :is="props.component" ref="componentRef" v-bind="bindProps">
    <template v-for="item in Object.keys(slots) " #[item]="data">
      <slot v-bind="data || {}" :name="item" />
    </template>
    <template v-if="props.childComponent">
      <component
        :is="props.childComponent"
        v-for="item in formatOptions"
        :key="item.value"
        :disabled="item.disabled"
        :label="item.label"
        :value="item.value"
      />
    </template>
    <template v-if="props.component.name === 'ElSelect'">
      <ElOption
        v-for="item in formatOptions"
        :key="item.value"
        :disabled="item.disabled"
        :label="item.label"
        :value="item.value!"
      />
    </template>
  </component>
</template>

<style lang="scss" scoped>

</style>
